import {
  AuditOutlined,
  BookOutlined,
  CalculatorOutlined,
  CarOutlined,
  CloudUploadOutlined,
  ContainerOutlined,
  FileProtectOutlined,
  FileSearchOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  GlobalOutlined,
  InboxOutlined,
  NumberOutlined,
  ProfileOutlined,
  ProjectOutlined,
  QrcodeOutlined,
  ReconciliationOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { Form, InputNumber } from 'antd';
import {
  Group,
  MasterSwitch,
  SettingSwitch,
} from '@/components/profile/settingCards';
import { cn } from '@/libs/utils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';

/**
 * The E-invoices tile of the company profile as setting cards. Same fields,
 * visibility rules and save as CompanyEInvoiceSettings, which steps aside on
 * the profile.
 */
export default function CompanyEInvoiceProfilePanel() {
  const ctx = useCompanyGeneralDataForm();
  const {
    autoArchiveAfterMonthsKeys,
    autoArchiveAfterMonthsState,
    autoArchiveInvoicesOn,
    eInvoiceChecked,
    eInvoiceKeys,
    eInvoiceState,
    readOnly,
    sectionClass,
    t,
  } = ctx;
  const f = (key) => t(key, { ns: 'forms' });
  const ui = (key) =>
    t(`companyProfile.cardsUi.${key}`, { ns: 'translations' });
  const lock = (state) => readOnly || !!state?.disabled;

  // `name` = the context prefix of <name>Keys / <name>State.
  const toggle = (name, props) => {
    const state = ctx[`${name}State`];
    return (
      state?.visible && (
        <Form.Item name={ctx[`${name}Keys`]} noStyle>
          <SettingSwitch locked={lock(state)} {...props} />
        </Form.Item>
      )
    );
  };

  const archiveMonths = autoArchiveInvoicesOn &&
    autoArchiveAfterMonthsState?.visible && (
      <div className="flex items-center gap-2 pl-[42px] text-[12px] text-zinc-500">
        {f('autoArchiveAfterMonths')}
        <Form.Item
          name={autoArchiveAfterMonthsKeys}
          className="!mb-0 [&_.ant-form-item-control-input]:!min-h-0"
          tooltip={f('autoArchiveAfterMonthsTooltip')}
          rules={[
            {
              type: 'integer',
              min: 1,
              max: 24,
              message: f('autoArchiveAfterMonthsRange'),
            },
          ]}
        >
          {lock(autoArchiveAfterMonthsState) ? (
            <ValueText />
          ) : (
            <InputNumber
              size="small"
              min={1}
              max={24}
              precision={0}
              className="w-16"
            />
          )}
        </Form.Item>
      </div>
    );

  if (!eInvoiceState?.visible) return null;

  return (
    <div className={cn('grid gap-4', sectionClass('efakture'))}>
      <Form.Item name={eInvoiceKeys} noStyle>
        <MasterSwitch
          locked={lock(eInvoiceState)}
          icon={<FileTextOutlined />}
          title={f('eInvoicePlatform')}
        />
      </Form.Item>

      {/* Mounted while switched off too, like the old block. */}
      <div className={cn('grid gap-4', !eInvoiceChecked && 'hidden')}>
        <Group title={ui('groupInvoices')}>
          {toggle('qrInEInvoices', {
            icon: <QrcodeOutlined />,
            title: f('qrInEInvoices'),
          })}
          {toggle('invoiceProjectEnabled', {
            icon: <ProjectOutlined />,
            title: f('invoiceProjectEnabled'),
          })}
          {toggle('manualPurchaseInvoiceEnabled', {
            icon: <FileSearchOutlined />,
            title: f('manualPurchaseInvoiceEnabled'),
          })}
          {toggle('advancedSalesInvoicingEnabled', {
            icon: <ShopOutlined />,
            title: f('advancedSalesInvoicingEnabled'),
          })}
          {toggle('showInternalRefNumber', {
            icon: <NumberOutlined />,
            title: f('showInternalRefNumber'),
          })}
        </Group>

        <Group title={ui('groupBooks')}>
          {toggle('invoiceJournalEnabled', {
            icon: <BookOutlined />,
            title: f('invoiceJournalEnabled'),
          })}
          {toggle('salesJournalEnabled', {
            icon: <ReconciliationOutlined />,
            title: f('salesJournalEnabled'),
          })}
          {toggle('vatNoticeEnabled', {
            icon: <AuditOutlined />,
            title: f('vatNoticeEnabled'),
          })}
          {toggle('postedToErpTrackingEnabled', {
            icon: <CloudUploadOutlined />,
            title: f('postedToErpTrackingEnabled'),
          })}
          {toggle('taxReturnsClientView', {
            icon: <FileProtectOutlined />,
            title: t('companySwitch', { ns: 'taxReturns' }),
          })}
          {toggle('ledgerCardsEnabled', {
            icon: <ProfileOutlined />,
            title: f('ledgerCardsEnabled'),
            hint: f('ledgerCardsEnabledTooltip'),
          })}
        </Group>

        <div>
          <Group title={f('sefStateDataGroup')}>
            {toggle('sefCustomsEnabled', {
              icon: <GlobalOutlined />,
              title: f('sefCustomsEnabled'),
              hint: f('sefCustomsEnabledTooltip'),
            })}
            {toggle('sefInputVatEnabled', {
              icon: <FileDoneOutlined />,
              title: f('sefInputVatEnabled'),
              hint: f('sefInputVatEnabledTooltip'),
            })}
            {toggle('sefVatRecordingEnabled', {
              icon: <CalculatorOutlined />,
              title: f('sefVatRecordingEnabled'),
              hint: f('sefVatRecordingEnabledTooltip'),
            })}
            {toggle('sefFiscalBillsEnabled', {
              icon: <CarOutlined />,
              title: f('sefFiscalBillsEnabled'),
              hint: f('sefFiscalBillsEnabledTooltip'),
            })}
          </Group>
          <p className="mb-0 mt-2 text-[12px] text-zinc-500">
            {f('sefStateDataGroupNote')}
          </p>
        </div>

        <Group title={ui('groupArchive')}>
          {toggle('autoArchiveInvoicesEnabled', {
            icon: <InboxOutlined />,
            title: f('autoArchiveInvoicesEnabled'),
            wide: !!archiveMonths,
            children: archiveMonths,
          })}
          {toggle('manualArchiveInvoicesEnabled', {
            icon: <ContainerOutlined />,
            title: f('manualArchiveInvoicesEnabled'),
          })}
        </Group>
      </div>
    </div>
  );
}

const ValueText = ({ value }) => (
  <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-bold text-zinc-800">
    {value ?? '—'}
  </span>
);
